import React from 'react';
import {
  Box,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Button,
  useColorModeValue,
  Badge,
  VStack,
  Icon,
  Stack,
  Flex,
  Link,
} from '@chakra-ui/react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const MotionBox = motion(Box);

const PricingCard = ({ title, price, features, unavailableFeatures, isPopular, variant, colorScheme, isComingSoon }) => {
  const bgColor = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.700`);
  const borderColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.300`);

  return (
    <MotionBox
      p={8}
      borderWidth="1px"
      borderRadius="xl"
      overflow="hidden"
      shadow="lg"
      bg={bgColor}
      transition="all 0.3s ease"
      _hover={{
        shadow: '2xl',
        borderColor: borderColor,
        transform: 'scale(1.05)',
      }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      position="relative"
      minHeight="600px"
    >
      {isPopular && (
        <Badge
          colorScheme="red"
          variant="solid"
          rounded="full"
          px={3}
          py={1}
          position="absolute"
          top={2}
          left={2}
          transform="rotate(-10deg)"
          boxShadow="md"
          zIndex={1}
        >
          Empfohlen
        </Badge>
      )}
      <Stack spacing={3} align="center" mb={4}>
        <Heading as="h3" size="lg" fontWeight="bold" mt={6}>
          {title}
        </Heading>
        <Text fontSize="4xl" fontWeight="extrabold" color={useColorModeValue(`${colorScheme}.600`, `${colorScheme}.300`)}>
          {isComingSoon ? 'Coming Soon' : price}
        </Text>
      </Stack>
      <VStack spacing={3} width="100%" align="start" px={4} flex="1">
        {features.map((feature, index) => (
          <Box key={index} display="flex" alignItems="center">
            <Icon
              as={FaCheckCircle}
              color="green.400"
              mr={2}
            />
            <Text fontWeight="bold">
              {feature}
            </Text>
          </Box>
        ))}
        {unavailableFeatures && unavailableFeatures.map((feature, index) => (
          <Box key={index} display="flex" alignItems="center">
            <Icon
              as={FaTimesCircle}
              color="gray.400"
              mr={2}
            />
            <Text color="gray.500">
              {feature}
            </Text>
          </Box>
        ))}
      </VStack>
      <Flex justifyContent="center" mt={6}>
        {isComingSoon ? (
          <Link href="mailto:admin@forgeideas.de">
            <Button
              colorScheme="teal"
              variant="solid"
              size="lg"
              rounded="full"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'xl',
              }}
            >
              Kontaktieren
            </Button>
          </Link>
        ) : (
          <Link href="https://github.com/storminator89/Zeiterfassung?tab=readme-ov-file#dependencies-and-resources">
            <Button
              colorScheme="teal"
              variant="solid"
              size="lg"
              rounded="full"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'xl',
              }}
            >
              Eigene Installation
            </Button>
          </Link>
        )}
      </Flex>
    </MotionBox>
  );
};

export default function Pricing() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const pricingPlans = [
    {
      title: 'Free / Self-Hosted',
      price: '€0 / Monat',
      features: [
        'Arbeitszeiterfassung',
        'Dashboard Statistiken',
        'Arbeitszeitübersicht',
        'Automatische Berechnung',
        'Mehrsprachigkeit',
        'Kalenderansicht',
        'Benutzer- und Abteilungsverwaltung',
        'Administrationsbereich',
        'Geolokalisierung',
        'Selbst-Hosting',
        'Manuelle Updates',
      ],
      unavailableFeatures: [
        'Cloud-Hosting',
        'Regelmäßige automatische Updates',
        'Premium Support',
      ],
      variant: 'standard',
      colorScheme: 'blue',
      isComingSoon: false,
    },
    {
      title: 'Cloud-Hosted Pro',
      price: '€1.99 / Benutzer / Monat',
      features: [
        'Arbeitszeiterfassung',
        'Dashboard Statistiken',
        'Arbeitszeitübersicht',
        'Automatische Berechnung',
        'Mehrsprachigkeit',
        'Kalenderansicht',
        'Benutzer- und Abteilungsverwaltung',
        'Administrationsbereich',
        'Geolokalisierung',
        'Selbst-Hosting',
        'Cloud-Hosting',
        'Regelmäßige automatische Updates',
        'Premium Support',
      ],
      isPopular: true,
      variant: 'featured',
      colorScheme: 'green',
      isComingSoon: true,
    },
  ];

  return (
    <MotionBox
      as="section"
      id="pricing"
      py={10}
      bg={useColorModeValue('gray.50', 'gray.900')}
      ref={ref}
      initial={{ opacity: 0 }}
      animate={inView ? { opacity: 1 } : {}}
      transition={{ duration: 0.8 }}
    >
      <Container maxW="7xl">
        <Heading
          as="h2"
          fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
          textAlign={'center'}
          mb={16}
          color={useColorModeValue('gray.800', 'white')}
        >
          Wählen Sie Ihren Plan
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
          {pricingPlans.map((plan, index) => (
            <PricingCard key={index} {...plan} />
          ))}
        </SimpleGrid>
      </Container>
    </MotionBox>
  );
}
